/**
 * examples-format.ts — pure (DOM-free) formatting helpers extracted from examples.ts.
 *
 * W-J seam extraction: these helpers were trapped inside the IIFE-wrapped examples.ts
 * module which reads window.* at top level, preventing direct ESM import and v8 coverage.
 * Moving them here makes them directly importable and unit-testable via vitest.
 *
 * All exports are PURE (input → string/value, no document/window reads).
 * examples.ts re-imports everything from this module.
 *
 * DO NOT add DOM imports, window reads, or fetch calls to this file — that would
 * re-trap the logic. DOM-touching functions (renderSampleCard, renderExpandedArticle,
 * wireAwaitingFilters, boot, fetchSampleChanges, loadCatalog, handleChangesClick)
 * remain in examples.ts.
 *
 * Epic: docs/epics/active/ts-unit-testability-seams-260623/main.md
 */
// ─── HTML escape helper ──────────────────────────────────────────────────────
//
// Inline copy of the adaEsc implementation from esc.ts (which is an IIFE
// non-module script and cannot be imported). Pure regex, no DOM dependency —
// this is what makes the test runtime possible without a browser stub.
// The two implementations must remain behaviorally identical; changes to
// esc.ts should be reflected here.
function escHtml(s) {
    return String(s == null ? "" : s)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}
// ─── Remediations-performed per-category standards claims ────────────────────
//
// Hardcoded demonstration data until the standards-bridge work surfaces real
// rule.Standards through the changes-emit pipeline. Exported so window.adaExamples
// can expose it for tests and downstream consumers.
export const REMEDIATION_CLAIMS_BY_FIXTURE = {
    "stanford-linear-programming": {
        "Alt text added": ["WCAG 1.1.1", "MATTERHORN-13-001", "OFFICE-PPTX-ALT-01"],
        "Slide title set": ["WCAG 2.4.2", "MATTERHORN-14-002", "OFFICE-PPTX-TITLE-01"],
        "Decorative shapes marked": ["WCAG 1.1.1", "MATTERHORN-13-004"],
        "Reading order corrected": ["WCAG 1.3.2", "MATTERHORN-09-001"],
        "Language tag set": ["WCAG 3.1.1"],
    },
    "unc-accessible-syllabus": {
        "Alt text added": ["WCAG 1.1.1", "OFFICE-DOCX-ALT-01"],
        "Document title set": ["WCAG 2.4.2", "OFFICE-DOCX-TITLE-01"],
        "Language tag set": ["WCAG 3.1.1"],
        "Image down-sampled": ["DocAble:ImageBandwidth"],
    },
    "sjsu-grade-template": {
        "Alt text added": ["WCAG 1.1.1", "OFFICE-XLSX-ALT-01"],
        "Workbook title set": ["WCAG 2.4.2", "OFFICE-XLSX-TITLE-01"],
        "Sheet name disambiguated": ["OFFICE-XLSX-SHEET-01"],
        "Language tag set": ["WCAG 3.1.1"],
    },
    "mit-ocw-8513-quantum-lec3": {
        "Alt text added": ["WCAG 1.1.1", "MATTERHORN-13-001", "PDFUA-1-7-3"],
        "Tag tree built": ["WCAG 1.3.1", "PDFUA-1-7-1", "MATTERHORN-01-001"],
        "Reading order fixed": ["WCAG 1.3.2", "MATTERHORN-09-001"],
        "Document title set": ["WCAG 2.4.2", "PDFUA-1-7-1"],
        "Language tag set": ["WCAG 3.1.1", "PDFUA-1-7-2"],
    },
    "mit-ocw-6046j-lec07": {
        "Alt text added": ["WCAG 1.1.1", "MATTERHORN-13-001", "PDFUA-1-7-3"],
        "Heading hierarchy fixed": ["WCAG 1.3.1", "WCAG 2.4.6", "PDFUA-1-7-4"],
        "Tag tree built": ["WCAG 1.3.1", "PDFUA-1-7-1", "MATTERHORN-01-001"],
        "Document title set": ["WCAG 2.4.2", "PDFUA-1-7-1"],
    },
    "arxiv-2208.03516": {
        "Alt text added": ["WCAG 1.1.1", "PDFUA-1-7-3", "MATTERHORN-13-001"],
        "Tag tree built": ["WCAG 1.3.1", "PDFUA-1-7-1"],
        "Table headers marked": ["WCAG 1.3.1", "MATTERHORN-15-001", "PDFUA-1-7-5"],
        "Document title set": ["WCAG 2.4.2", "PDFUA-1-7-1"],
        "Language tag set": ["WCAG 3.1.1", "PDFUA-1-7-2"],
    },
};
// ─── Pure formatting functions ───────────────────────────────────────────────
/**
 * Map a standards string ("WCAG 1.1.1", "OFFICE-PPTX", "MATTERHORN-1-001",
 * "PDFUA-7.5", "DocAble:ContrastResolutionQuality-3") to a CSS class for
 * color coding. Default = "wcag".
 *
 * DocAble (Q2 settlement, 260513) uses ":" as the namespace separator
 * and gets its own color class; the colon split is handled before the
 * space/hyphen split to avoid mis-classifying as the bare "DOCABLE" head.
 */
export function classForStandard(s) {
    if (typeof s !== "string")
        return "wcag";
    if (s.startsWith("DocAble:"))
        return "docable";
    const head = (s.split(/[\s\-]/, 1)[0] ?? '').toUpperCase();
    if (head === "WCAG")
        return "wcag";
    if (head === "MATTERHORN")
        return "mh";
    if (head === "OFFICE")
        return "office";
    if (head === "PDFUA")
        return "pdfua";
    return "wcag";
}
/**
 * Sole-source display filter (Q2 settlement, 260513).
 *
 * Mirrors the server-side FilterForDisplay predicate:
 * when at least one external claim is present, suppress DocAble. When the
 * list is DocAble-only, return it unchanged.
 *
 * Operates on the flat string form ("WCAG 1.1.1" / "DocAble:Foo" / etc.)
 * that the existing renderer consumes — a claim is "external" iff it does
 * NOT begin with the "DocAble:" prefix.
 */
export function filterClaimsForDisplay(claims) {
    if (!Array.isArray(claims) || claims.length === 0)
        return [];
    let hasExternal = false;
    for (let i = 0; i < claims.length; i++) {
        if (typeof claims[i] === "string" && !claims[i].startsWith("DocAble:")) {
            hasExternal = true;
            break;
        }
    }
    if (!hasExternal)
        return claims.slice();
    return claims.filter((c) => typeof c === "string" && !c.startsWith("DocAble:"));
}
/**
 * Render an inline badge row from a claim-string list. Applies the
 * sole-source filter before rendering — DocAble entries are suppressed
 * when any external claim is present on the same item.
 *
 * Returns "" (empty string) when the filtered list is empty.
 */
export function renderStandardsBadgeRow(claims) {
    const filtered = filterClaimsForDisplay(claims);
    if (filtered.length === 0)
        return "";
    return filtered
        .map((s) => `<span class="standards-badge ${classForStandard(s)}">${escHtml(s)}</span>`)
        .join("");
}
// ─── Standards claims keyed by CANONICAL category label ──────────────────────
//
// The per-fixture map above (REMEDIATION_CLAIMS_BY_FIXTURE) was authored against
// hypothetical category labels ("Alt text added", "Slide title set", …) that the
// backend changelog derivation NEVER emits — so it matched ZERO live categories
// on every fixture and the Remediations-table badges never rendered (Phase 22 RCA,
// 260704). The category LABELS the backend actually produces are the stable,
// format-agnostic canonical strings defined in
// backend/src/AdaTool.RemediationReporting/ChangeLog.cs (the `Categories` table's
// `.Label` values). This map keys on those real labels so demonstration badges
// render for every fixture whose live categories are covered here. It is the
// primary lookup; the per-fixture map is a per-fixture override that still wins
// when present.
//
// NOTE: this remains DEMONSTRATION data (hardcoded standards attributions) until
// the standards-bridge surfaces real rule.Standards through the changes-emit
// pipeline (examples.ts §"through /api/examples/{id}/changes … follow-up").
export const REMEDIATION_CLAIMS_BY_CATEGORY = {
    "Alt text added": ["WCAG 1.1.1", "MATTERHORN-13-001"],
    "Decorative shapes marked": ["WCAG 1.1.1", "MATTERHORN-13-004"],
    "Language set": ["WCAG 3.1.1"],
    "Slide titles fixed": ["WCAG 2.4.2"],
    "Presentation title set": ["WCAG 2.4.2", "OFFICE-PPTX-TITLE-01"],
    "Table headers fixed": ["WCAG 1.3.1", "MATTERHORN-15-001"],
    "Tag tree created": ["WCAG 1.3.1", "PDFUA-1-7-1", "MATTERHORN-01-001"],
    "Tag tree bootstrapped": ["WCAG 1.3.1", "PDFUA-1-7-1"],
    "MarkInfo set": ["PDFUA-1-6-2"],
    "Viewer preferences set": ["WCAG 2.4.2", "PDFUA-1-7-1"],
    "Tab order fixed": ["WCAG 1.3.2", "PDFUA-1-7-1"],
    "Reading order fixed": ["WCAG 1.3.2", "MATTERHORN-09-001"],
    "Bookmarks added": ["WCAG 2.4.5", "PDFUA-1-7-1"],
    "ActualText populated": ["WCAG 1.1.1", "PDFUA-1-7-3"],
    "Character encoding fixed": ["WCAG 1.3.1", "PDFUA-1-7-2"],
    "Links tagged": ["WCAG 1.3.1", "PDFUA-1-7-1"],
    "Link text fixed": ["WCAG 2.4.4"],
    "Heading hierarchy fixed": ["WCAG 1.3.1", "WCAG 2.4.6", "PDFUA-1-7-4"],
    "Page labels added": ["WCAG 2.4.5"],
    "OCR text layer added": ["WCAG 1.4.5"],
    "Media transcribed": ["WCAG 1.2.2"],
};
/**
 * Look up the standards-claim list for a (fixtureId, categoryLabel) pair.
 *
 * Resolution order: (1) the per-fixture override map keyed on the fixture id;
 * (2) the canonical-label map keyed on the backend's real category label. The
 * canonical fallback is what makes badges render for live fixtures — the
 * per-fixture map's invented labels never match the real changelog categories
 * (Phase 22 RCA, 260704). Returns [] when neither yields a hit — caller renders
 * no badges.
 */
export function remediationClaimsFor(fixtureId, categoryLabel) {
    const byCat = REMEDIATION_CLAIMS_BY_FIXTURE[fixtureId];
    if (byCat) {
        const claims = byCat[categoryLabel];
        if (Array.isArray(claims))
            return claims;
    }
    const canonical = REMEDIATION_CLAIMS_BY_CATEGORY[categoryLabel];
    return Array.isArray(canonical) ? canonical : [];
}
/**
 * Render an SVG file-type icon by format key.
 * Returns an SVG <use href="#icon-{format}"> string suitable for innerHTML.
 */
export function fileIconHtml(format) {
    const id = {
        pdf: "icon-pdf",
        pptx: "icon-pptx",
        docx: "icon-docx",
        xlsx: "icon-xlsx",
    }[format] ?? "icon-pdf";
    return `<svg class="file-icon" width="26" height="26" aria-hidden="true" focusable="false"><use href="#${id}"/></svg>`;
}
/**
 * Returns a short human-readable string like "31s" or "2m 11s".
 * Returns null when ms is falsy or rounds to < 1s, so callers can skip display.
 */
export function fmtProcessingTime(ms) {
    if (!ms)
        return null;
    const totalSec = Math.round(ms / 1000);
    if (totalSec < 1)
        return null;
    if (totalSec < 60)
        return `${totalSec}s`;
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return s === 0 ? `${m}m` : `${m}m ${s}s`;
}
/**
 * Three-tier classification based on the after-value relative to before:
 *   clean    — after == 0
 *   improved — after > 0 AND reduction > 50%
 *   stuck    — after > 0 AND reduction <= 50%
 *
 * Returns a className suffix (e.g. "clean" → "fix-count-clean" /
 * "score-after-clean") or null when before/after are not both numbers.
 *
 * null / undefined / non-numeric strings must NOT coerce to 0 here — they
 * signal "data unavailable" and should produce null. Number(null) === 0
 * would mis-classify as clean.
 */
export function defectTier(before, after) {
    if (before == null || after == null)
        return null;
    const b = Number(before);
    const a = Number(after);
    if (!Number.isFinite(b) || !Number.isFinite(a))
        return null;
    if (a === 0)
        return "clean";
    if (b > 0 && (b - a) / b > 0.5)
        return "improved";
    return "stuck";
}
/** Format a file-format key as its display label ("pdf" → "PDF", etc.). */
export function formatLabel(format) {
    switch (format) {
        case "pdf": return "PDF";
        case "pptx": return "PPTX";
        case "docx": return "DOCX";
        case "xlsx": return "XLSX";
        default: return (format || "").toUpperCase();
    }
}
/**
 * Return the plural/singular unit word for a format's content unit.
 *   pptx → slide/slides
 *   xlsx → sheet/sheets
 *   others → page/pages
 */
export function unitWord(format, count) {
    if (format === "pptx")
        return count === 1 ? "slide" : "slides";
    if (format === "xlsx")
        return count === 1 ? "sheet" : "sheets";
    return count === 1 ? "page" : "pages";
}
/**
 * Collect ALL changes across categories, stamping each item with the category
 * name if absent. Used both for per-analysis standards rollup and the
 * sample-changes section.
 */
export function flattenChanges(cats) {
    const out = [];
    if (!Array.isArray(cats))
        return out;
    for (const c of cats) {
        const items = Array.isArray(c.changes) ? c.changes : [];
        for (const it of items) {
            if (!it.category && (c.category || c.label)) {
                out.push(Object.assign({}, it, { category: c.category || c.label || '' }));
            }
            else {
                out.push(it);
            }
        }
    }
    return out;
}
/**
 * Date-only format ("May 12, 2026") from an ISO timestamp string.
 * Falls back to the raw ISO string on parse failure.
 */
export function fmtDate(iso) {
    try {
        return new Date(iso).toLocaleDateString("en-US", {
            year: "numeric", month: "long", day: "numeric",
        });
    }
    catch (_) {
        return iso;
    }
}
